export type BadgeScope = 'user' | 'team'

/** User or team badge with when it was earned (for display in modal). */
export interface EarnedBadge {
  badge: Badge
  earnedAt: Date
}

export interface BadgeArgument {
  id: string
  challengeID: string
  name: string
  description: string
  icon: string
  activityType?: string
  requirementValue: number
  requirementType: 'count' | 'time' | 'distance' | 'streak'
  requirementUnit?: string
  requirementDisplayValue?: number | null
  isActive: boolean
  level?: 'Bronze' | 'Silver' | 'Gold' | 'Platinum' | 'Diamond'
  cumulative: boolean
  scope: BadgeScope
}

export default class Badge {
  readonly id: string
  readonly challengeID: string
  readonly name: string
  readonly description: string
  readonly icon: string
  readonly activityType?: string
  readonly requirementValue: number
  readonly requirementType: 'count' | 'time' | 'distance' | 'streak'
  readonly requirementUnit?: string
  readonly requirementDisplayValue?: number | null
  readonly isActive: boolean
  readonly level?: 'Bronze' | 'Silver' | 'Gold' | 'Platinum' | 'Diamond'
  readonly cumulative: boolean
  readonly scope: BadgeScope

  constructor(arg: BadgeArgument) {
    this.id = arg.id
    this.challengeID = arg.challengeID
    this.name = arg.name
    this.description = arg.description
    this.icon = arg.icon
    this.activityType = arg.activityType
    this.requirementValue = arg.requirementValue
    this.requirementType = arg.requirementType
    this.requirementUnit = arg.requirementUnit
    this.requirementDisplayValue = arg.requirementDisplayValue ?? null
    this.isActive = arg.isActive
    this.level = arg.level
    this.cumulative = arg.cumulative
    this.scope = arg.scope
  }

  static fromPG(data: any): Badge {
    return new Badge({
      id: data.id,
      challengeID: data.challenge_id,
      name: data.name,
      description: data.description,
      icon: data.icon ?? '',
      activityType: data.activity_type ?? data.activityType,
      requirementValue: Number(data.requirement_value ?? data.requirementValue ?? 0),
      requirementType: data.requirement_type ?? data.requirementType,
      requirementUnit: data.requirement_unit ?? data.requirementUnit,
      requirementDisplayValue:
        data.requirement_display_value != null
          ? Number(data.requirement_display_value)
          : data.requirementDisplayValue != null
            ? Number(data.requirementDisplayValue)
            : null,
      isActive: data.is_active ?? data.isActive ?? true,
      level: data.level,
      cumulative: data.cumulative === true || data.cumulative === 'TRUE',
      scope: data.scope ?? 'user',
    })
  }

  getRequirementDescription(): string {
    const value = this.requirementDisplayValue ?? this.requirementValue
    const unit = this.requirementUnit || this.getDefaultUnit()
    const rmLabel = unit === 'RM' ? (value !== 1 ? 'RMs' : 'RM') : unit

    switch (this.requirementType) {
      case 'count':
        return `${value} ${unit}`
      case 'time':
      case 'distance':
        return `${value} ${rmLabel}`
      case 'streak':
        return `${value} day${value !== 1 ? 's' : ''} in a row`
      default:
        return `${value} ${rmLabel}`
    }
  }

  private getDefaultUnit(): string {
    switch (this.requirementType) {
      case 'count':
        return 'activities'
      case 'time':
        return 'hours'
      case 'distance':
        return 'miles'
      case 'streak':
        return 'days'
      default:
        return 'units'
    }
  }

  static bucketSlug(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]/g, '')
  }

  appliesToActivity(
    activityID: string,
    activity?: { bucketName?: string | null; bucketID?: string | null },
  ): boolean {
    if (!this.activityType || this.activityType === 'any') return true
    if (this.activityType === activityID) return true
    if (activity?.bucketName) {
      return Badge.bucketSlug(activity.bucketName) === Badge.bucketSlug(this.activityType)
    }
    return false
  }

  checkRequirementsMet(
    currentActivityUnits: number,
    cumulativeUnits: number,
    activityID?: string,
    activity?: { bucketName: string | null },
  ): boolean {
    if (activityID && !this.appliesToActivity(activityID, activity)) {
      return false
    }

    const valueToCheck = this.cumulative ? cumulativeUnits : currentActivityUnits
    return valueToCheck >= this.requirementValue
  }

  static compareByActivityTypeAndValue(a: Badge, b: Badge): number {
    const activityTypeOrder: Record<string, number> = { any: 0 }
    const aGroup = activityTypeOrder[a.activityType ?? ''] ?? 1
    const bGroup = activityTypeOrder[b.activityType ?? ''] ?? 1
    if (aGroup !== bGroup) return aGroup - bGroup
    const typeCompare = (a.activityType ?? '').localeCompare(b.activityType ?? '')
    if (typeCompare !== 0) return typeCompare
    return b.requirementValue - a.requirementValue
  }

  static getLevelColor(level?: string): string {
    switch (level) {
      case 'Bronze':
        return '#CD7F32'
      case 'Silver':
        return '#C0C0C0'
      case 'Gold':
        return '#FFD700'
      case 'Platinum':
        return '#E5E4E2'
      case 'Diamond':
        return '#B9F2FF'
      default:
        return '#9E9E9E'
    }
  }
}
